/**
 * Network Monitor
 * Detects network status and triggers auto-retry on reconnection
 */

class NetworkMonitor {
  constructor() {
    this.isOnline = navigator.onLine;
    this.listeners = [];
    this.reconnectCallbacks = [];

    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  handleOnline() {
    const wasOffline = !this.isOnline;
    this.isOnline = true;

    // Notify listeners
    this.listeners.forEach(callback => callback(true));

    // Trigger reconnect callbacks
    if (wasOffline) {
      this.reconnectCallbacks.forEach(callback => callback());
    }
  }

  handleOffline() {
    this.isOnline = false;
    this.listeners.forEach(callback => callback(false));
  }

  /**
   * Subscribe to network status changes
   * Callback receives boolean: true = online, false = offline
   */
  subscribe(callback) {
    this.listeners.push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Register callback to run when connection is restored
   */
  onReconnect(callback) {
    this.reconnectCallbacks.push(callback);

    // Return unregister function
    return () => {
      this.reconnectCallbacks = this.reconnectCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Get current network status
   */
  getStatus() {
    return this.isOnline;
  }

  /**
   * Check if currently online
   */
  isConnected() {
    return this.isOnline && navigator.onLine;
  }

  /**
   * Wait for network to come back online
   * Returns promise that resolves when online
   */
  waitForNetwork(timeout = null) {
    return new Promise((resolve, reject) => {
      if (this.isOnline) {
        resolve();
        return;
      }

      let timeoutId;
      const unsubscribe = this.subscribe((status) => {
        if (status) {
          unsubscribe();
          if (timeoutId) clearTimeout(timeoutId);
          resolve();
        }
      });

      if (timeout) {
        timeoutId = setTimeout(() => {
          unsubscribe();
          reject(new Error(`Network not restored within ${timeout}ms`));
        }, timeout);
      }
    });
  }

  /**
   * Cleanup listeners
   */
  destroy() {
    this.listeners = [];
    this.reconnectCallbacks = [];
  }
}

// Singleton instance
let instance = null;

export function getNetworkMonitor() {
  if (!instance) {
    instance = new NetworkMonitor();
  }
  return instance;
}

export default NetworkMonitor;
